import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import States from 'constants/states';
import { Field, FieldProps, useFormikContext } from 'formik';
import React from 'react';
import { LessonForm } from '../types';
import StepContainer from './StepContainer';

const PersonalInformation: React.FC = () => {
  const { setFieldValue } = useFormikContext();

  const handleDisplayInfoChange = (value: boolean) => (_: React.ChangeEvent<{}>) => {
    setFieldValue('personalInformation.displayInfo', value);
  };

  return (
    <StepContainer>
      <Grid item>
        <Typography gutterBottom>Tell us about yourself:</Typography>
      </Grid>
      <Grid item>
        <Field name="personalInformation.name">
          {({ field, meta }: FieldProps<LessonForm['personalInformation']['name']>) => (
            <TextField
              id="name"
              label="Your Name (First and Last)"
              required
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              helperText={meta.touched && meta.error}
              error={meta.touched && Boolean(meta.error)}
              {...field}
            />
          )}
        </Field>
      </Grid>
      <Grid item>
        <Field name="personalInformation.email">
          {({ field, meta }: FieldProps<LessonForm['personalInformation']['email']>) => (
            <TextField
              id="email"
              label="Email Address"
              required
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              helperText={meta.touched && meta.error}
              error={meta.touched && Boolean(meta.error)}
              {...field}
            />
          )}
        </Field>
      </Grid>
      <Grid item>
        <Field name="personalInformation.twitter">
          {({ field, meta }: FieldProps<LessonForm['personalInformation']['twitter']>) => (
            <TextField
              id="twitter"
              label="Twitter Handle"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              helperText={meta.touched && meta.error}
              error={meta.touched && Boolean(meta.error)}
              {...field}
            />
          )}
        </Field>
      </Grid>
      <Grid item>
        <Field name="personalInformation.school.name">
          {({ field, meta }: FieldProps<LessonForm['personalInformation']['school']['name']>) => (
            <TextField
              id="schoolName"
              label="School Name"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              helperText={meta.touched && meta.error}
              error={meta.touched && Boolean(meta.error)}
              {...field}
            />
          )}
        </Field>
      </Grid>
      <Grid item>
        <Field name="personalInformation.school.district">
          {({
            field,
            meta,
          }: FieldProps<LessonForm['personalInformation']['school']['district']>) => (
            <TextField
              id="schoolDistrict"
              label="School District"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              helperText={meta.touched && meta.error}
              error={meta.touched && Boolean(meta.error)}
              {...field}
            />
          )}
        </Field>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={6}>
          <Field name="personalInformation.school.zip">
            {({ field, meta }: FieldProps<LessonForm['personalInformation']['school']['zip']>) => (
              <TextField
                id="schoolZip"
                label="School Zip Code"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={meta.touched && meta.error}
                error={meta.touched && Boolean(meta.error)}
                {...field}
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={6}>
          <Field name="personalInformation.school.state">
            {({
              field,
              meta,
            }: FieldProps<LessonForm['personalInformation']['school']['state']>) => (
              <FormControl error={meta.touched && Boolean(meta.error)} fullWidth>
                <InputLabel shrink id="schoolStateLabel">
                  School State
                </InputLabel>
                <Select labelId="schoolStateLabel" id="schoolState" displayEmpty {...field}>
                  {States.map((state, index) => (
                    <MenuItem key={index} value={state.abbreviation}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{meta.touched && meta.error}</FormHelperText>
              </FormControl>
            )}
          </Field>
        </Grid>
      </Grid>
      <Grid item>
        <Field name="personalInformation.displayInfo">
          {({ field, meta }: FieldProps<LessonForm['personalInformation']['displayInfo']>) => (
            <FormControl error={meta.touched && Boolean(meta.error)} component="fieldset" required>
              <FormLabel>
                Would you like your name and school information to be displayed with your lesson?
              </FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Radio
                      checked={field.value === true}
                      onChange={handleDisplayInfoChange(true)}
                    />
                  }
                  label="Yes, please display my name and school information."
                />
                <FormControlLabel
                  control={
                    <Radio
                      {...field}
                      checked={field.value === false}
                      onChange={handleDisplayInfoChange(false)}
                    />
                  }
                  label="No, I prefer for my submission to remain anonymous."
                />
              </FormGroup>
              <FormHelperText>
                {meta.touched && Boolean(meta.error)
                  ? meta.error
                  : "While we do not currently display each educator's name and school information along with lessons, we plan to add this feature in the future. Your email address and contact information will not be displayed without your permission."}
              </FormHelperText>
            </FormControl>
          )}
        </Field>
      </Grid>
    </StepContainer>
  );
};

export default PersonalInformation;
